import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import { Check } from "lucide-react";

interface StorybookSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const features = [
  "Their name",
  "Their best friend's name",
  "Their favorite sport or activity",
  "Their most-loved cartoon characters or heroes"
];

const culturalElements = [
  "Legends of the majestic Armenian mountains",
  "Adventures in ancient Armenian castles and villages",
  "Melodies of Armenian lullabies woven into the tale",
  "Values of courage, kindness, and resilience—deeply rooted in Armenian folklore"
];

export function StorybookSheet({ isOpen, onClose }: StorybookSheetProps) {
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div className="px-4">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-3 tracking-wide font-cinzel">
            Storybook Creation
          </h2>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            A Personalized Tale with Every Order
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 text-center"
        >
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            At TEL U ASEGH, we believe in crafting not just clothing but unforgettable experiences. Inspired by the rich storytelling traditions of Armenian culture, we bring you a one-of-a-kind storybook that transforms every purchase into a magical journey.
          </p>
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-8"
        >
          <h3 className="text-lg font-cinzel font-bold text-primary mb-4 text-center">How It Works</h3>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Step 1 */}
            <div className="bg-accent/5 rounded-lg p-6">
              <h4 className="font-medium mb-4">1. Share Your Child's Details</h4>
              <ul className="space-y-2">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 mt-1 text-primary flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Step 2 */}
            <div className="bg-accent/5 rounded-lg p-6">
              <h4 className="font-medium mb-4">2. Cultural Elements</h4>
              <ul className="space-y-2">
                {culturalElements.map((element, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 mt-1 text-primary flex-shrink-0" />
                    <span>{element}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-primary/5 backdrop-blur-sm rounded-2xl p-6 text-center"
        >
          <p className="text-sm text-muted-foreground mb-3">
            Ready to create a magical story for your child? Our creative team is here to help.
          </p>
          <p className="text-sm font-medium text-primary">
            Contact us at{" "}
            <a 
              href="mailto:cs@teluasegh.com"
              className="hover:underline"
            >
              cs@teluasegh.com
            </a>
          </p>
        </motion.div>
      </div>
    </BottomSheet>
  );
} 